import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import languages from 'utilities/language'
import styled, { ThemeContext } from 'styled-components/macro'
import Text from 'components/atoms/text'

const Container = styled.label`
  align-items: center;
  display: flex;
  cursor: pointer;
`

// Native input kept in the DOM for accessibility and form behaviour,
// but visually hidden as we render a custom circle next to it (RDC only).
const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`

// Plain native radio for non-RDC themes using browser default styling.
const NativeInput = styled.input`
  flex: 0 0 auto;
  margin: 0 ${({ theme }) => theme.sizings.lvl2} 0 0;
  min-width: auto;
  cursor: pointer;
`

const CircleIndicator = styled.span`
  flex: 0 0 auto;
  box-sizing: border-box;
  width: ${({ theme }) => theme.sizeByFactor(2.5)};
  height: ${({ theme }) => theme.sizeByFactor(2.5)};
  margin: 0 ${({ theme }) => theme.sizings.lvl2} 0 0;
  border-radius: 50%;
  border: ${({ theme }) => theme.sizeByFactor(0.25)} solid ${({ theme }) => theme.colors.brandCharlie};
  background: ${({ theme }) => theme.colors.sectionBackground};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 120ms ease-in-out,
    background-color 120ms ease-in-out;

  ${HiddenInput}:checked + & {
    border-width: ${({ theme }) => theme.sizeByFactor(0.75)};
    border-color: ${({ theme }) => theme.colors.radioSelected};
  }

  ${HiddenInput}:focus-visible + & {
    outline: ${({ theme }) => theme.sizeByFactor(0.25)} solid ${({ theme }) => theme.colors.radioSelected};
    outline-offset: ${({ theme }) => theme.sizeByFactor(0.25)};
  }
`

const Label = styled(Text)`
  &::first-letter {
    text-transform: capitalize;
  }
`

function RadioButton({
  checked,
  id,
  name,
  onChange,
  title,
  value,
  ...restProps
}) {
  const languageCode = languages.getLanguage()
  const theme = useContext(ThemeContext)
  const useCustomStyling = Boolean(theme?.colors?.radioSelected)

  return (
    <Container htmlFor={id} {...restProps}>
      {useCustomStyling ? (
        <>
          <HiddenInput
            checked={checked}
            id={id}
            name={name}
            onChange={onChange}
            type="radio"
            value={value}
          />
          <CircleIndicator aria-hidden="true" />
        </>
      ) : (
        <NativeInput
          checked={checked}
          id={id}
          name={name}
          onChange={onChange}
          type="radio"
          value={value}
        />
      )}
      <Label lang={languageCode} tag="span" type="input" text={title} />
    </Container>
  )
}

RadioButton.propTypes = {
  checked: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.string,
}

RadioButton.defaultProps = {
  checked: false,
  id: null,
  value: '',
}

export default RadioButton
